import { supabase } from '../config/supabase';

export interface MedicalRecordGeneratorInput {
  nome: string;
  sobrenome: string;
  dataNascimento: string;
  sexo: 'M' | 'F';
  anoCriacao: number;
}

export const generateMedicalRecordNumber = (input: MedicalRecordGeneratorInput): string => {
  const { nome, sobrenome, dataNascimento, sexo, anoCriacao } = input;

  try {
    const birthDate = new Date(dataNascimento);

    if (isNaN(birthDate.getTime())) {
      throw new Error('Data de nascimento inválida');
    }

    const year = birthDate.getFullYear().toString().slice(-2);
    const month = (birthDate.getMonth() + 1).toString().padStart(2, '0');
    const day = birthDate.getDate().toString().padStart(2, '0');

    const dateCode = `${year}${month}${day}`;

    const cleanName = nome.trim().replace(/[^a-zA-ZÀ-ÿ\s]/g, '');
    const cleanSurname = sobrenome.trim().replace(/[^a-zA-ZÀ-ÿ\s]/g, '');

    const firstInitial = cleanName.charAt(0).toUpperCase();
    const lastInitial = cleanSurname.charAt(0).toUpperCase();
    const initials = `${firstInitial}${lastInitial}`;

    const yearCode = anoCriacao.toString();

    const medicalRecordNumber = `${dateCode}${sexo}${yearCode}${initials}`;

    return medicalRecordNumber;
  } catch (error: any) {
    console.error('Erro ao gerar código do prontuário:', error);
    throw new Error(`Não foi possível gerar o código do prontuário: ${error.message}`);
  }
};

export const checkMedicalRecordExists = async (medicalRecordNumber: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('patients')
      .select('medical_record_number')
      .eq('medical_record_number', medicalRecordNumber)
      .limit(1);
    if (error) throw error;
    return data.length > 0;
  } catch (error) {
    console.error('Erro ao verificar existência do prontuário:', error);
    return false;
  }
};

export const generateUniqueMedicalRecordNumber = async (
  input: MedicalRecordGeneratorInput
): Promise<string> => {
  let medicalRecordNumber = generateMedicalRecordNumber(input);
  let exists = await checkMedicalRecordExists(medicalRecordNumber);

  let suffix = 1;
  while (exists) {
    medicalRecordNumber = `${generateMedicalRecordNumber(input)}-${suffix}`;
    exists = await checkMedicalRecordExists(medicalRecordNumber);
    suffix++;

    if (suffix > 100) {
      throw new Error('Não foi possível gerar um código único após 100 tentativas');
    }
  }

  return medicalRecordNumber;
};

export const parseBirthDateFromMedicalRecord = (medicalRecordNumber: string): string | null => {
  try {
    if (medicalRecordNumber.length < 6) {
      return null;
    }

    const year = medicalRecordNumber.substring(0, 2);
    const month = medicalRecordNumber.substring(2, 4);
    const day = medicalRecordNumber.substring(4, 6);

    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const yearNum = parseInt(year);

    let fullYear: number;
    if (yearNum > (currentYear % 100)) {
      fullYear = currentCentury - 100 + yearNum;
    } else {
      fullYear = currentCentury + yearNum;
    }

    const birthDate = new Date(fullYear, parseInt(month) - 1, parseInt(day));

    if (isNaN(birthDate.getTime())) {
      return null;
    }

    return birthDate.toISOString().split('T')[0];
  } catch (error) {
    console.error('Erro ao extrair data de nascimento do prontuário:', error);
    return null;
  }
};

export const extractGenderFromMedicalRecord = (medicalRecordNumber: string): 'M' | 'F' | null => {
  try {
    if (medicalRecordNumber.length < 7) {
      return null;
    }

    const gender = medicalRecordNumber.charAt(6).toUpperCase();

    if (gender === 'M' || gender === 'F') {
      return gender;
    }

    return null;
  } catch (error) {
    console.error('Erro ao extrair sexo do prontuário:', error);
    return null;
  }
};
